import { i18n } from "next-i18next";

import { getApolloClient } from "lib/server/getApolloClient";
import { sharedGetServerSideProps } from "lib/server/sharedGetServerSideProps";

import PropertyListing from "components/pages/property/listing";
import { WWW_BUILDING_LISTING_QUERY } from "components/pages/property/listing/graphql";

export async function getServerSideProps(ctx) {
  // Unfortunately our URL structure isn't very RESTful, so we're seeing a lot
  // of bots crawling URLs like /destinations/search/wedding, which our routing
  // interprets "search" as the ad_name and "wedding" as the id.
  if (!ctx.params.id.match(/^c?\d+$/)) return { notFound: true };

  const response = await sharedGetServerSideProps(ctx, {
    translationNamespaces: ["buildingMetaTitles", "listing", "search"],
  });
  const { data, redirectToSearch } = await getListing(
    ctx,
    response.props.bootstrap,
  );
  const listing = data?.buildingListing;

  if (redirectToSearch || !listing) {
    const url = `/destinations/${ctx.params.city}/search`;
    return { redirect: { destination: url, permanent: false } };
  }

  const isL1 = listing.__typename === "BuildingListing";

  if (!isL1 && listing.l1Url) {
    // In case there are any issues with the redirects, we want to prevent the
    // default behaviour of browsers caching permanent redirects.
    ctx.res.setHeader("Cache-Control", "no-store, no-cache, must-revalidate");
    return { redirect: { destination: listing.l1Url, permanent: true } };
  }

  return {
    props: {
      ...response.props,
      listing,
      headerProps: getHeaderProps(listing),
    },
  };
}

// Retrieve Listing data
async function getListing(ctx, bootstrap) {
  const redirectErrorCodes = ["NOT_FOUND", "INVALID_ARGUMENTS"];
  const client = getApolloClient({ ctx, bootstrap, logBody: false });

  return await client
    .query({
      query: WWW_BUILDING_LISTING_QUERY,
      variables: { id: ctx.params.id },
    })
    .catch(error => {
      // Check error code in gql response - Error code:
      // https://github.com/Flatbook/flatbook/blob/main/app/apis/graphql/types/errors.rb
      if (
        error.graphQLErrors.some(e =>
          redirectErrorCodes.includes(e.extensions?.code),
        )
      ) {
        return {
          redirectToSearch: true,
        };
      }

      throw error;
    });
}

function getHeaderProps(listing) {
  const stateOrCountryCode =
    listing.countryCode === "US" ? listing.stateCode : listing.countryCode;

  const sharedPropertiesAndStrings = {
    interpolation: { escapeValue: false },
    buildingName: listing.buildingName,
    city: listing.cityName,
  };

  const title = listing.isHotel
    ? i18n.t("listing:header.title.hotel", {
        ...sharedPropertiesAndStrings,
        stateOrCountryCode,
      })
    : i18n.t("listing:header.title.nonHotel", sharedPropertiesAndStrings);

  const description = i18n.t(
    `listing:header.description.${listing.isHotel ? "hotel" : "nonHotel"}`,
    {
      ...sharedPropertiesAndStrings,
      neighborhood: listing.neighborhoodName,
    },
  );

  const ogImage =
    listing.photos?.length > 0 ? listing.photos[0]?.largeSrc : null;

  return {
    title,
    ...(description ? { description } : undefined),
    ogTitle: title,
    ogDescription: listing.shortDescription,
    ogImage,
  };
}

export default PropertyListing;
